import {
  Box,
  HStack,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { useEffect, useRef, useState, KeyboardEvent } from "react";
import { Heading22 } from "components/Heading";
import { useUpdatePath } from "features/Path/hooks";
import { Path } from "features/Path/models";
import AssignmentPill from "./AssignmentPill";
import IconPicker from "./IconPicker";
import IconTile from "./IconTile";
import LastUsed from "./LastUsed";

interface EditablePathHeaderProps {
  path: Path;
}

export default function EditablePathHeader({ path }: EditablePathHeaderProps) {
  const { mutate: update } = useUpdatePath({ pathId: path.id, silent: true });

  // --- name edit -----------------------------------------------------------
  // draftName is only meaningful while editing; we re-seed it at the moment edit mode opens
  // (no useEffect prop-sync, which would be cascading state).
  const [isEditingName, setIsEditingName] = useState(false);
  const [draftName, setDraftName] = useState(path.name);
  const inputRef = useRef<HTMLInputElement>(null);

  const startEditing = () => {
    setDraftName(path.name);
    setIsEditingName(true);
  };

  useEffect(() => {
    if (isEditingName) {
      // microtask so the input is mounted before we select it
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [isEditingName]);

  const commitName = () => {
    const trimmed = draftName.trim();
    if (!trimmed || trimmed === path.name) {
      setIsEditingName(false);
      return;
    }
    update({ name: trimmed, autoAdd: path.autoAdd });
    setIsEditingName(false);
  };

  const cancelName = () => {
    setIsEditingName(false);
  };

  const onNameKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitName();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelName();
    }
  };

  // --- icon edit (popover) -------------------------------------------------
  // Uncontrolled — let Chakra manage open state so PopoverTrigger's click
  // handler isn't competing with our own.
  const handleIconChange = ({ icon, color }: { icon: string; color: string }) => {
    if (icon === path.icon && color === path.iconColor) return;
    update({
      name: path.name,
      autoAdd: path.autoAdd,
      icon,
      iconColor: color,
    });
  };

  return (
    <HStack spacing="4" align="center" minW={0}>
      <Popover placement="bottom-start" gutter={6}>
        <PopoverTrigger>
          <Box
            as="button"
            type="button"
            borderRadius="md"
            display="inline-flex"
            flexShrink={0}
            transition="opacity 120ms"
            _hover={{ opacity: 0.85 }}
            sx={{
              outline: "none",
              "&:focus-visible": {
                outline: "2px solid var(--chakra-colors-cdsIndigo-500)",
                outlineOffset: "1px",
              },
            }}
            aria-label="Change path icon"
          >
            <IconTile icon={path.icon} color={path.iconColor} size={48} />
          </Box>
        </PopoverTrigger>
        <PopoverContent w="360px">
          <PopoverArrow />
          <PopoverBody p="4">
            <IconPicker
              icon={path.icon}
              color={path.iconColor}
              previewName={path.name}
              onChange={handleIconChange}
            />
          </PopoverBody>
        </PopoverContent>
      </Popover>

      <Box minW={0} flex="1">
        {isEditingName ? (
          <Input
            ref={inputRef}
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={commitName}
            onKeyDown={onNameKeyDown}
            size="lg"
            variant="flushed"
            fontWeight="bold"
            fontSize="22px"
            aria-label="Path name"
          />
        ) : (
          <Tooltip label="Click to rename" placement="top" hasArrow openDelay={400}>
            <Box
              role="button"
              tabIndex={0}
              onClick={startEditing}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  startEditing();
                }
              }}
              borderRadius="sm"
              px="1"
              mx="-1"
              cursor="text"
              _hover={{ bg: "gray.50" }}
            >
              <Heading22 size="md" noOfLines={1}>{path.name}</Heading22>
            </Box>
          </Tooltip>
        )}

        <HStack spacing="3" mt="1" color="gray.500" fontSize="sm" wrap="wrap">
          <AssignmentPill autoAdd={path.autoAdd} />
          <Text as="span">
            {path.stepsCount}
            {" "}
            {path.stepsCount === 1 ? "step" : "steps"}
          </Text>
          <Text as="span" color="gray.300">·</Text>
          <HStack spacing="1.5" as="span">
            <Text as="span">Last assigned</Text>
            <LastUsed lastUsedAt={path.lastUsedAt} />
          </HStack>
        </HStack>
      </Box>
    </HStack>
  );
}
